import { LoadingState } from '#/components/common/loading-state'
import { toast } from 'sonner'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { AppHeader } from '#/components/layout/app-header'
import { Mascot } from '#/components/layout/mascot'
import { Button } from '#/components/ui/button'
import { Input } from '#/components/ui/input'
import { Progress } from '#/components/ui/progress'
import { ChoiceCard } from '#/components/plan/choice-card'
import { api } from '#/lib/api'
import { sendStoredAttribution } from '#/lib/api-misc'
import { useHydratedSession } from '#/lib/auth-client'
import { refreshBuilderSession } from '#/lib/simple-mode'
import { m } from '#/paraglide/messages'
import { Briefcase, Check, Code2, Copy, GraduationCap, HeartHandshake, Rocket, Search, Users, Youtube, Instagram, Twitter, MoreHorizontal } from 'lucide-react'
import { useCopy } from '#/components/common/prompt-block'

export const Route = createFileRoute('/onboarding')({ validateSearch: z.object({ redirect: z.string().optional() }), component: Onboarding })

/** 3-step onboarding (spec/01 §12). Guests → sign-in; already-onboarded users → straight to `redirect` (default `/home`). */
function Onboarding() {
  const navigate = useNavigate()
  const { redirect } = Route.useSearch()
  const target = (redirect && redirect.startsWith('/') ? redirect : '/home') as '/home'
  const { data: session, isPending } = useHydratedSession()
  const user = session?.user as { id: string; name: string; email: string; onboardedAt?: string | Date | null } | undefined
  const [step, setStep] = useState(1)
  const [name, setName] = useState('')
  const [source, setSource] = useState<string>('')
  const [ctx, setCtx] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const attributed = useRef(false)
  useEffect(() => {
    if (isPending) return
    if (!user) {
      navigate({ to: '/sign-in', search: { redirect: `/onboarding${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''}` } })
      return
    }
    if (user.onboardedAt) {
      navigate({ to: target, replace: true })
      return
    }
    if (!name && user.name) setName(user.name)
    if (!attributed.current) {
      attributed.current = true
      void sendStoredAttribution()
    }
  }, [isPending, user, navigate, target, redirect, name])
  const finish = async (skipped = false) => {
    if (saving) return
    setSaving(true)
    try {
      const builderMode = ctx === 'nontech' ? 'simple' : undefined
      await api('/api/user/onboarding', { method: 'POST', json: skipped ? { skipped: true } : { name, referralSource: source, context: ctx, builderMode } })
      await refreshBuilderSession()
      await navigate({ to: target, replace: true })
    } catch { toast.error(m.common_error_generic()) }
    finally { setSaving(false) }
  }
  const sources = [['youtube', m.onb_2_youtube(), <Youtube key="y" className="size-5" />], ['instagram', m.onb_2_instagram(), <Instagram key="i" className="size-5" />], ['twitter', m.onb_2_twitter(), <Twitter key="t" className="size-5" />], ['friend', m.onb_2_friend(), <Users key="f" className="size-5" />], ['google', m.onb_2_google(), <Search key="g" className="size-5" />], ['other', m.onb_2_other(), <MoreHorizontal key="o" className="size-5" />]] as const
  const ctxs = [['founder', m.onb_3_founder(), <Rocket key="r" className="size-5" />], ['dev', m.onb_3_dev(), <Code2 key="c" className="size-5" />], ['pm', m.onb_3_pm(), <Briefcase key="b" className="size-5" />], ['student', m.onb_3_student(), <GraduationCap key="s" className="size-5" />], ['nontech', m.simple_onb_nontech(), <HeartHandshake key="n" className="size-5" />]] as const
  if (isPending || !user || user.onboardedAt) {
    return (
      <>
        <AppHeader showStats={false} />
        <main className="flex-1 overflow-y-auto"><LoadingState label={m.loading_session()} /></main>
      </>
    )
  }
  return (
    <>
      <AppHeader showStats={false} />
      <main className="flex-1 overflow-y-auto px-6 py-10">
        <div className="mx-auto w-full max-w-xl space-y-6" data-testid="onboarding">
          <div className="flex items-center justify-between text-xs text-muted-foreground"><span>{m.onb_step({ n: step, total: 3 })}</span><button disabled={saving} aria-busy={saving} onClick={() => void finish(true)} className="hover:text-foreground" data-testid="onb-skip">{saving ? m.common_saving() : m.onb_skip()}</button></div>
          <Progress value={(step / 3) * 100} className="h-1.5" />
          {step === 1 ? (
            <div className="space-y-4">
              <Mascot size={96} eager />
              <h1 className="text-3xl font-bold">{m.onb_1_title()}</h1>
              <p className="text-muted-foreground">{m.onb_1_subtitle()}</p>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={m.onb_1_name()} className="h-11" data-testid="onb-name" />
              <Button className="h-11" disabled={!name.trim()} onClick={() => setStep(2)} data-testid="onb-next">{m.common_next()}</Button>
            </div>
          ) : step === 2 ? (
            <div className="space-y-4">
              <h1 className="text-3xl font-bold">{m.onb_2_title()}</h1>
              <div className="grid gap-3 sm:grid-cols-2">{sources.map(([k, label, icon]) => <ChoiceCard key={k} icon={icon} title={label} selected={source === k} onClick={() => { setSource(k); setStep(3) }} />)}</div>
            </div>
          ) : (
            <div className="space-y-4">
              <h1 className="text-3xl font-bold">{m.onb_3_title()}</h1>
              <div className="grid gap-3 sm:grid-cols-2">{ctxs.map(([k, label, icon]) => <ChoiceCard key={k} icon={icon} title={label} description={k === 'nontech' ? m.simple_onb_nontech_desc() : undefined} selected={ctx === k} onClick={() => setCtx(k)} />)}</div>
              <Button className="h-11" disabled={!ctx || saving} loading={saving} loadingLabel={m.common_saving()} onClick={() => void finish()} data-testid="onb-finish">{m.onb_finish()}</Button>
              {/* Inspirasi F0 — ready-to-paste sentence for people who bring their own chat assistant (ChatGPT/Claude/Gemini) */}
              <GuidePromptLine />
            </div>
          )}
        </div>
      </main>
    </>
  )
}

/** Small copyable line: "Read https://bystep.dev/agent-guide.md first, then guide me step by step." */
function GuidePromptLine() {
  const { copied, copy } = useCopy()
  const text = m.simple_guide_prompt()
  return (
    <div className="space-y-1.5 rounded-xl border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground" data-testid="onb-guide-prompt">
      <p>{m.simple_guide_onb_line()}</p>
      <div className="flex items-start gap-2">
        <code className="min-w-0 flex-1 whitespace-pre-wrap break-words font-mono text-[11px] text-foreground">{text}</code>
        <Button type="button" size="sm" variant="secondary" className="h-7 shrink-0 px-2" onClick={() => void copy(text)} aria-label={m.common_copy()}>
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </Button>
      </div>
    </div>
  )
}
